"use strict";

import {roundf} from '../../js-utilities';
import {inchesToFeetAndInches, weightFromHeightAndBmi} from './calculator-helpers';

/*
 * Although any constraints can be specified directly in the exported function or this object,
 *   some of them are a little easier to keep track of separately here
 */
const metricConstants = {
    hdl: {
        min: 0.7,
        max: 2.3
    },
    ldl: {
        min: 1.0,
        max: 6.0
    },
    tg: {
        min: 0.5,
        max: 18.1,
    },
    waist: {
        min: {female: 58, male: 66},
        max: {female: 127, male: 140},
    }
};

const imperialConversions = {
    cm_per_inch: 2.54,
    kg_per_lb: 1 / 2.204623, // Easier to keep track of numbers with fewer decimal places and then divide from 1
    vitd_nmolL_per_ngml: 2.5,
    lipids_mmolL_per_mgdl: 1 / 38.67, // Easier to keep track of numbers with fewer decimal places and then divide from 1
};

// Generic reusable function for converting from metric to imperial and round to the specified precision
function convert(inputValue, imperialConversion, metricPrecision, imperialPrecision, values, forceMetric) {
    if (values.imperial && !forceMetric) {
        return roundf(inputValue / imperialConversion, imperialPrecision);
    }
    return roundf(inputValue, metricPrecision);
}

export default function constraints(language) {

    const constraints = {
        birthday: {
            min: 15,
            max: 120,
            precision: 0,
            message: language.constraint_age
        },
        systolic_blood_pressure: {
            min: 80,
            max: 180,
            precision: 0,
            message: language.constraint_systolic_blood_pressure
        },
        diastolic_blood_pressure: {
            min: 40,
            max: 109,
            precision: 0,
            message: language.constraint_diastolic_blood_pressure
        },
        fmd: {
            min: 0,
            max: 20,
            precision: 0,
            message: language.constraint_fmd
        },
        smoking: {
            min: 0,
            max: 40,
            precision: 0,
            message: language.constraint_smoking
        },
        // cm / inches
        waist: {
            min: (values, forceMetric) => convert(values.gender ? metricConstants.waist.min.female : metricConstants.waist.min.male, imperialConversions.cm_per_inch, 0, 0, values, forceMetric), // female = 1, male = 0
            max: (values, forceMetric) => convert(values.gender ? metricConstants.waist.max.female : metricConstants.waist.max.male, imperialConversions.cm_per_inch, 0, 0, values, forceMetric), // female = 1, male = 0
            precision: 0,
            imperialConversion: imperialConversions.cm_per_inch,
            message: (values, forceMetric) => language.constraint_waist
                .replace('{min}', constraints.waist.min(values, forceMetric))
                .replace('{max}', constraints.waist.max(values, forceMetric))
        },
        // 4'0' - 8'0'
        height: {
            min: (values, forceMetric) => convert(121.92, imperialConversions.cm_per_inch, 0, 0, values, forceMetric),
            max: (values, forceMetric) => convert(243.84, imperialConversions.cm_per_inch, 0, 0, values, forceMetric),
            precision: 0,
            imperialConversion: imperialConversions.cm_per_inch,
            message: (values, forceMetric) => language.constraint_height
                .replace('{min}', ((values.imperial && !forceMetric) ? inchesToFeetAndInches(constraints.height.min(values, forceMetric)) : constraints.height.min(values, forceMetric)))
                .replace('{max}', ((values.imperial && !forceMetric) ? inchesToFeetAndInches(constraints.height.max(values, forceMetric)) : constraints.height.max(values, forceMetric)))
        },
        weight: {
            min: (values, forceMetric) => convert(weightFromHeightAndBmi(values.height || constraints.height.min(values, true), constraints.bmi.min), imperialConversions.kg_per_lb, 0, 0, values, forceMetric),
            max: (values, forceMetric) => convert(weightFromHeightAndBmi(values.height || constraints.height.max(values, true), constraints.bmi.max), imperialConversions.kg_per_lb, 0, 0, values, forceMetric),
            precision: 0,
            imperialConversion: imperialConversions.kg_per_lb,
            message: (values, forceMetric) => language.constraint_weight
                .replace('{min}', constraints.weight.min(values, forceMetric))
                .replace('{max}', constraints.weight.max(values, forceMetric))
        },
        bmi: {
            min: 18.5,
            max: 60,
            precision: 1,
            message: language.constraint_bmi
        },
        a1c: {
            min: 4,
            max: 9,
            precision: 1,
            message: language.constraint_a1c
        },
        // ng/ml / nmol/L (Vitamin D)
        vitamin_d: {
            min: (values, forceMetric) => convert(30, imperialConversions.vitd_nmolL_per_ngml, 0, 0, values, forceMetric),
            max: (values, forceMetric) => convert(90, imperialConversions.vitd_nmolL_per_ngml, 0, 0, values, forceMetric),
            precision: 0,
            imperialConversion: imperialConversions.vitd_nmolL_per_ngml,
            message: (values, forceMetric) => language.constraint_vitamin_d
                .replace('{min}', constraints.waist.min(values, forceMetric))
                .replace('{max}', constraints.waist.max(values, forceMetric))
        },
        // mg/dl mmol/L (Cholesterol)
        lipids_hdlc: {
            min: (values, forceMetric) => convert(metricConstants.hdl.min, imperialConversions.lipids_mmolL_per_mgdl, 1, 0, values, forceMetric),
            max: (values, forceMetric) => convert(metricConstants.hdl.max, imperialConversions.lipids_mmolL_per_mgdl, 1, 0, values, forceMetric),
            precision: (values, forceMetric) => (!values.imperial || forceMetric) ? 1 : 0,
            imperialConversion: imperialConversions.lipids_mmolL_per_mgdl,
            message: (values, forceMetric) => language.constraint_lipids_hdlc
                .replace('{min}', constraints.lipids_hdlc.min(values, forceMetric))
                .replace('{max}', constraints.lipids_hdlc.max(values, forceMetric))
        },
        // mg/dl mmol/L (Cholesterol)
        lipids_ldlc: {
            min: (values, forceMetric) => convert(metricConstants.ldl.min, imperialConversions.lipids_mmolL_per_mgdl, 1, 0, values, forceMetric),
            max: (values, forceMetric) => convert(metricConstants.ldl.max, imperialConversions.lipids_mmolL_per_mgdl, 1, 0, values, forceMetric),
            precision: (values, forceMetric) => (!values.imperial || forceMetric) ? 1 : 0,
            imperialConversion: imperialConversions.lipids_mmolL_per_mgdl,
            message: (values, forceMetric) => language.constraint_lipids_ldlc
                .replace('{min}', constraints.lipids_ldlc.min(values, forceMetric))
                .replace('{max}', constraints.lipids_ldlc.max(values, forceMetric))
        },
        // mg/dl mmol/L (Cholesterol)
        lipids_tg: {
            min: (values, forceMetric) => convert(metricConstants.tg.min, imperialConversions.lipids_mmolL_per_mgdl, 1, 0, values, forceMetric),
            max: (values, forceMetric) => convert(metricConstants.tg.max, imperialConversions.lipids_mmolL_per_mgdl, 1, 0, values, forceMetric),
            precision: (values, forceMetric) => (!values.imperial || forceMetric) ? 1 : 0,
            imperialConversion: imperialConversions.lipids_mmolL_per_mgdl,
            message: (values, forceMetric) => language.constraint_lipids_tg
                .replace('{min}', constraints.lipids_tg.min(values, forceMetric))
                .replace('{max}', constraints.lipids_tg.max(values, forceMetric))
        }
    };

    return constraints;
}
